// Schritt 06 — CAD-Plan (Lücke aus dem Planer-Konzept).
// Bemaßter Plan-Vorschau + Export der Dachgeometrie als DXF/GeoJSON (getestete
// roof-core-Engine). PDF/DWG sind als Folgeausbau gekennzeichnet (ehrlich).
import { useEffect, useState } from "react";
import { apiClient } from "@/lib/api-client.js";
import {
  polygonsToDxf,
  roofDesignToGeoJSON,
  latLngPolysToLocalMeters,
  type DxfPolyline,
} from "@energyos/roof-core";

interface LL { lat: number; lng: number }
interface RoofSurface { roofType: string; polygon?: LL[] }
interface Building { footprintPolygon?: LL[]; roofSurfaces?: RoofSurface[] }

function downloadText(filename: string, text: string, mime: string) {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function CadPlanStep({ projectId }: { projectId: string }) {
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    void (async () => {
      const res = await apiClient.get<{ buildings: Building[] }>(`/projects/${projectId}/geospatial`);
      if (!alive) return;
      if (res.success) setBuildings(res.data?.buildings ?? []);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [projectId]);

  const collect = () => {
    const roofs: LL[][] = [];
    const obstacles: LL[][] = [];
    let footprint: LL[] | undefined;
    for (const b of buildings) {
      if (b.footprintPolygon && b.footprintPolygon.length >= 3 && !footprint) footprint = b.footprintPolygon;
      for (const r of b.roofSurfaces ?? []) {
        if ((r.polygon?.length ?? 0) >= 3) (r.roofType === "obstacle" ? obstacles : roofs).push(r.polygon!);
      }
    }
    return { roofs, obstacles, footprint };
  };

  const hasGeometry = (() => {
    const { roofs, obstacles, footprint } = collect();
    return !!footprint || roofs.length > 0 || obstacles.length > 0;
  })();

  const exportDxf = () => {
    const { roofs, obstacles, footprint } = collect();
    const groups: Array<{ layer: string; color: number; ll: LL[] }> = [];
    if (footprint) groups.push({ layer: "GEBAEUDE", color: 1, ll: footprint });
    roofs.forEach((r) => groups.push({ layer: "DACH", color: 5, ll: r }));
    obstacles.forEach((o) => groups.push({ layer: "STOERFLAECHE", color: 30, ll: o }));
    const { local } = latLngPolysToLocalMeters(groups.map((g) => g.ll));
    const polylines: DxfPolyline[] = groups.map((g, i) => ({ layer: g.layer, color: g.color, points: local[i] ?? [] }));
    downloadText(`cad-plan-${projectId}.dxf`, polygonsToDxf(polylines), "application/dxf");
  };
  const exportGeoJson = () => {
    const { roofs, obstacles, footprint } = collect();
    const fc = roofDesignToGeoJSON({ ...(footprint ? { footprint } : {}), roofs, obstacles });
    downloadText(`cad-plan-${projectId}.geojson`, JSON.stringify(fc, null, 2), "application/geo+json");
  };

  if (loading) return <div style={{ padding: 24, color: "#64748b" }}>Lade Dachgeometrie…</div>;

  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <span style={{ width: 11, height: 11, background: "#1e3a5f", borderRadius: 2 }} />
        <h3 style={{ fontSize: 13.5, fontWeight: 700, margin: 0 }}>CAD-Plan & Export</h3>
      </div>
      <p style={{ fontSize: 12.5, color: "#64748b", margin: "0 0 14px" }}>
        Exportiert Gebäude-Grundriss, Dachflächen und Störflächen als bemaßte CAD-Geometrie (Meter,
        Layer GEBÄUDE/DACH/STÖRFLÄCHE) für AutoCAD bzw. als GeoJSON-Austauschformat.
      </p>
      {!hasGeometry ? (
        <div style={{ padding: 20, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, color: "#92400e", fontSize: 13 }}>
          ⚠ Keine Dachgeometrie vorhanden — bitte zuerst in Schritt 02 (Dach) den Grundriss und die
          Dachflächen zeichnen.
        </div>
      ) : (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button type="button" className="btn-primary" style={{ fontSize: 13 }} onClick={exportDxf}>⬇ DXF (CAD)</button>
          <button type="button" className="btn-secondary" style={{ fontSize: 13 }} onClick={exportGeoJson}>⬇ GeoJSON</button>
          <button type="button" className="btn-secondary" style={{ fontSize: 13, opacity: 0.6, cursor: "not-allowed" }} disabled title="Folgeausbau">PDF / DWG (geplant)</button>
        </div>
      )}
    </div>
  );
}
